import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export const ServerUrl = "https://www.api.dearlanguage.com";

export type DailyWordItem = {
  id: number;

  headergerman: string;
  headertamil: string;
  headerenglish: string;
  headersinhala: string;
  headerhindi: string;
  headerarabic: string;
  headerturkish: string;
  headerrussian: string;
  headerchinese: string;
  headerfrench: string;
  headerspanish: string;

  subheadergerman: string;
  subheadertamil: string;
  subheaderenglish: string;
  subheadersinhala: string;
  subheaderhindi: string;
  subheaderarabic: string;
  subheaderturkish: string;
  subheaderrussian: string;
  subheaderchinese: string;
  subheaderfrench: string;
  subheaderspanish: string;

  wordgerman: string;
  wordtamil: string;
  wordenglish: string;
  wordsinhala: string;
  wordhindi: string;
  wordarabic: string;
  wordturkish: string;
  wordrussian: string;
  wordchinese: string;
  wordfrench: string;
  wordspanish: string;

  sentencegerman: string;
  sentencetamil: string;
  sentenceenglish: string;
  sentencesinhala: string;
  sentencehindi: string;
  sentencearabic: string;
  sentenceturkish: string;
  sentencerussian: string;
  sentencechinese: string;
  sentencefrench: string;
  sentencespanish: string;

  image: string | null;

  created_at?: string | null;
  updated_at?: string | null;

  [key: string]: unknown;
};

export type DailyWordFetchState = {
  dailyWord: DailyWordItem | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  todayId: number;
  refreshDailyWord: () => Promise<void>;
  retryDailyWord: () => Promise<void>;
};

let inMemoryTodayWord: DailyWordItem | null = null;
let inMemoryTodayId: number | null = null;

const REQUEST_TIMEOUT_MS = 15000;

const normalizeString = (value: unknown): string => {
  return typeof value === "string" ? value : "";
};

const normalizeNullableString = (value: unknown): string | null => {
  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }

  return null;
};

export const isLeapYear = (year: number): boolean => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

export const getDayOfYear = (date: Date): number => {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff =
    date.getTime() -
    start.getTime() +
    (start.getTimezoneOffset() - date.getTimezoneOffset()) * 60 * 1000;

  return Math.floor(diff / (1000 * 60 * 60 * 24)) - 1;
};

export const getTodayDailyWordId = (date: Date = new Date()): number => {
  const year = date.getFullYear();
  const leap = isLeapYear(year);
  const dayOfYear = getDayOfYear(date);

  if (leap) {
    return dayOfYear + 1;
  }

  if (date.getMonth() === 1 && date.getDate() === 29) {
    return 59;
  }

  if (dayOfYear <= 58) {
    return dayOfYear + 1;
  }

  return dayOfYear + 2;
};

const normalizeDailyWord = (raw: unknown): DailyWordItem | null => {
  const item =
    raw &&
    typeof raw === "object" &&
    (raw as { data?: unknown }).data &&
    typeof (raw as { data?: unknown }).data === "object"
      ? ((raw as { data: Record<string, unknown> }).data as Record<
          string,
          unknown
        >)
      : raw && typeof raw === "object"
      ? (raw as Record<string, unknown>)
      : null;

  if (!item) {
    return null;
  }

  const normalized: DailyWordItem = {
    ...item,

    id:
      typeof item.id === "number"
        ? item.id
        : typeof item.id === "string"
        ? Number(item.id) || 0
        : 0,

    headergerman: normalizeString(item.headergerman),
    headertamil: normalizeString(item.headertamil),
    headerenglish: normalizeString(item.headerenglish),
    headersinhala: normalizeString(item.headersinhala),
    headerhindi: normalizeString(item.headerhindi),
    headerarabic: normalizeString(item.headerarabic),
    headerturkish: normalizeString(item.headerturkish),
    headerrussian: normalizeString(item.headerrussian),
    headerchinese: normalizeString(item.headerchinese),
    headerfrench: normalizeString(item.headerfrench),
    headerspanish: normalizeString(item.headerspanish),

    subheadergerman: normalizeString(item.subheadergerman),
    subheadertamil: normalizeString(item.subheadertamil),
    subheaderenglish: normalizeString(item.subheaderenglish),
    subheadersinhala: normalizeString(item.subheadersinhala),
    subheaderhindi: normalizeString(item.subheaderhindi),
    subheaderarabic: normalizeString(item.subheaderarabic),
    subheaderturkish: normalizeString(item.subheaderturkish),
    subheaderrussian: normalizeString(item.subheaderrussian),
    subheaderchinese: normalizeString(item.subheaderchinese),
    subheaderfrench: normalizeString(item.subheaderfrench),
    subheaderspanish: normalizeString(item.subheaderspanish),

    wordgerman: normalizeString(item.wordgerman),
    wordtamil: normalizeString(item.wordtamil),
    wordenglish: normalizeString(item.wordenglish),
    wordsinhala: normalizeString(item.wordsinhala),
    wordhindi: normalizeString(item.wordhindi),
    wordarabic: normalizeString(item.wordarabic),
    wordturkish: normalizeString(item.wordturkish),
    wordrussian: normalizeString(item.wordrussian),
    wordchinese: normalizeString(item.wordchinese),
    wordfrench: normalizeString(item.wordfrench),
    wordspanish: normalizeString(item.wordspanish),

    sentencegerman: normalizeString(item.sentencegerman),
    sentencetamil: normalizeString(item.sentencetamil),
    sentenceenglish: normalizeString(item.sentenceenglish),
    sentencesinhala: normalizeString(item.sentencesinhala),
    sentencehindi: normalizeString(item.sentencehindi),
    sentencearabic: normalizeString(item.sentencearabic),
    sentenceturkish: normalizeString(item.sentenceturkish),
    sentencerussian: normalizeString(item.sentencerussian),
    sentencechinese: normalizeString(item.sentencechinese),
    sentencefrench: normalizeString(item.sentencefrench),
    sentencespanish: normalizeString(item.sentencespanish),

    image: normalizeNullableString(item.image),

    created_at: normalizeNullableString(item.created_at),
    updated_at: normalizeNullableString(item.updated_at),
  };

  if (!normalized.wordgerman && !normalized.sentencegerman) {
    return null;
  }

  return normalized;
};

const fetchWithTimeout = async (
  url: string,
  timeoutMs: number = REQUEST_TIMEOUT_MS
): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      method: "GET",
      signal: controller.signal,
      headers: {
        Accept: "application/json",
      },
      cache: "no-store",
    });

    window.clearTimeout(timeoutId);
    return response;
  } catch (error) {
    window.clearTimeout(timeoutId);
    throw error;
  }
};

export const fetchTodayDailyWord = async (
  todayId: number = getTodayDailyWordId()
): Promise<DailyWordItem | null> => {
  const response = await fetchWithTimeout(
    `${ServerUrl}/api/dailywords/${todayId}`,
    REQUEST_TIMEOUT_MS
  );

  if (!response.ok) {
    throw new Error(`DailyWord request failed with status ${response.status}`);
  }

  const payload = await response.json();
  const dailyWord = normalizeDailyWord(payload);

  if (!dailyWord) {
    throw new Error("Invalid DailyWord response from server");
  }

  inMemoryTodayWord = dailyWord;
  inMemoryTodayId = todayId;

  return dailyWord;
};

export const getDailyWordsImageUrl = (
  image: string | null | undefined
): string | null => {
  if (!image) {
    return null;
  }

  if (image.startsWith("http://") || image.startsWith("https://")) {
    return image;
  }

  if (image.startsWith("/")) {
    return `${ServerUrl}${image}`;
  }

  return `${ServerUrl}/storage/${image}`;
};

export const useDailyWordsData = (): DailyWordFetchState => {
  const todayId = useMemo(() => getTodayDailyWordId(), []);

  const [dailyWord, setDailyWord] = useState<DailyWordItem | null>(() => {
    if (inMemoryTodayId === todayId && inMemoryTodayWord) {
      return inMemoryTodayWord;
    }

    return null;
  });

  const [loading, setLoading] = useState<boolean>(() => {
    return !(inMemoryTodayId === todayId && inMemoryTodayWord);
  });

  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState<boolean>(() => {
    return Boolean(inMemoryTodayId === todayId && inMemoryTodayWord);
  });

  const isFetchingRef = useRef<boolean>(false);

  const loadTodayWord = useCallback(async (): Promise<void> => {
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const fetchedWord = await fetchTodayDailyWord(todayId);

      if (fetchedWord) {
        setDailyWord(fetchedWord);
      }
    } catch (fetchError) {
      const message =
        fetchError instanceof Error ? fetchError.message : String(fetchError);

      setError(message);
    } finally {
      setLoading(false);
      setInitialized(true);
      isFetchingRef.current = false;
    }
  }, [todayId]);

  useEffect(() => {
    if (inMemoryTodayId === todayId && inMemoryTodayWord) {
      setDailyWord(inMemoryTodayWord);
      setLoading(false);
      setError(null);
      setInitialized(true);
      return;
    }

    void loadTodayWord();
  }, [loadTodayWord, todayId]);

  const refreshDailyWord = useCallback(async (): Promise<void> => {
    await loadTodayWord();
  }, [loadTodayWord]);

  const retryDailyWord = useCallback(async (): Promise<void> => {
    setError(null);
    await loadTodayWord();
  }, [loadTodayWord]);

  return {
    dailyWord,
    loading,
    error,
    initialized,
    todayId,
    refreshDailyWord,
    retryDailyWord,
  };
};

export default useDailyWordsData;